import React, { useEffect, useState, useRef, useCallback, useContext } from "react";
import {
  Box,
  Button,
  Grid,
  useMediaQuery,
  useTheme,
  Typography,
} from "@mui/material";
import moment from "moment";
import {
  getNotifications,
  updateNotificationCount,
  getNotificationCount,
} from "../../Utils/apiService";
// import { useNavigate } from "react-router-dom";
// import notify_loader from "../../Assets/notification.svg";
import emptyNotification from "../../Assets/emptyNotification.png";
import { AuthContext } from "../../Helpers/Contexts/authContext";
import { AuthUser } from "../../interfaces/Contexts";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import defultNotification from "../../Assets/Icons/home.svg";
// import image from "../../Assets/facilitiescart.png";

interface INotification {
  id: any;
  day: any;
  body: string;
  url: string;
  topic: string;
  imageUrl?: string;
  timestamp: number;
  type: any;
  userId?: string;
  seenStatus?: boolean;
  title: string;
  description?: string;
}

interface IGroupedNotifications {
  today: INotification[];
  yesterday: INotification[];
  thisWeek: INotification[];
  earlier: INotification[];
}



const AppNotifications: React.FC<any> = ({
  handleClosePopover,
  openpop = true,
  onNotificationCountUpdate,
}) => {
  const {
    state: { user },
  } = useContext(AuthContext);
  const userData = user as AuthUser;
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  // const navigate = useNavigate();

  // State management
  const [perpage] = useState(20);
  const [curPage, setCurPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [groupedNotifications, setGroupedNotifications] = useState<IGroupedNotifications>({
    today: [],
    yesterday: [],
    thisWeek: [],
    earlier: [],
  });
  const [tabIndex, setTabIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  // const [message] = useState(""); // Removed unused variable
  const [totalPages, setTotalPages] = useState(0);
  const [notificationsList, setNotificationsList] = useState<INotification[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState<any[]>([]);
  const autoMarkedRef = useRef<Set<string>>(new Set());
  const scrollPositionRef = useRef<number>(0);
  const debounceTimeoutRef = useRef<number | null>(null);

  const userid = userData?.applicantId;
  const [isActiveMsg, setIsActiveMsg] = useState<string>("All");

  const handleMessageCategory = (text: string) => {
    setIsActiveMsg(text);
    // Map the old category to new tab index
    if (text === "All") setTabIndex(0);
    else if (text === "Unread") setTabIndex(1);
    else if (text === "Read") setTabIndex(2);
  };

  // Helper to mark first N unread notifications as read (auto)
  const markFirstNUnreadAsRead = useCallback(async (n: number) => {
    const unread = notificationsList
      .filter(noti => !noti.seenStatus && !autoMarkedRef.current.has(noti.id))
      .slice(0, n);

    if (unread.length === 0) return;
    const markPayload = unread.map(noti => ({ [noti.id]: true }));
    try {
      await updateNotificationCount(markPayload);
      setUnreadNotifications(prev =>
        prev.filter(id => !unread.some(noti => noti.id === id))
      );
      // Mark as auto-marked to avoid duplicate marking
      for (const noti of unread) {
        autoMarkedRef.current.add(noti.id);
      }
      // Optionally update notification count in parent
      if (onNotificationCountUpdate) {
        onNotificationCountUpdate(
          unreadNotifications.length - unread.length
        );
      }
    } catch (err) {
      console.error("Error marking notifications as read:", err);
    }
  }, [notificationsList, unreadNotifications, onNotificationCountUpdate]);

  const fetchData = useCallback(async (page = 1, resetData = false) => {
    try {
      if (!userid) {
        setLoading(false);
        return;
      }
      
      if (isFetchingMore && !resetData) {
        return; // Prevent duplicate calls
      }
      
      const tabType = tabIndex === 0 ? "alert" : "notification";
      const container = document.getElementById("scrollable-container");
      const currentScrollTop = container ? container.scrollTop : 0;
      
      // Fetch data from API
      const res = await getNotifications(page, perpage, userid, tabType);

      if (res?.data.status === "success") {
        setTotalPages(res?.data.totalPages || 0);
        const notificationsObj = res?.data?.data?.[0]?.notifications || {};
        const notificationsArray = Object.entries(notificationsObj).map(
          ([id, notification]: [string, any]) => ({
            id,
            ...notification,
          })
        );

        setNotificationsList((prevList) => {
          // If resetData is true (tab switch), replace the list, otherwise append
          const updatedList = resetData ? notificationsArray : [...prevList, ...notificationsArray];
          setGroupedNotifications(groupNotificationsByDate(updatedList));
          return updatedList;
        });
        
        const newUnreadIds = notificationsArray
          .filter((notif) => !notif.seenStatus)
          .map((notif) => notif.id);

        setUnreadNotifications((prevUnread) => {
          // If resetData is true, replace unread list, otherwise merge
          const updatedUnread = resetData ? newUnreadIds : [...Array.from(new Set([...prevUnread, ...newUnreadIds]))];
          return updatedUnread;
        });

        if (!resetData) {
          requestAnimationFrame(() => {
            if (container) {
              container.scrollTop = currentScrollTop;
            }
          });
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setIsFetchingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupNotificationsByDate = (notificationsList: INotification[]) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(today.getDate() - today.getDay());
    
    const groupedNotifications = {
      today: [] as INotification[],
      yesterday: [] as INotification[],
      thisWeek: [] as INotification[],
      earlier: [] as INotification[],
    };
    
    notificationsList.forEach((notification) => {
      const notificationDate = new Date(notification.timestamp);
      if (notificationDate.toDateString() === today.toDateString()) {
        groupedNotifications.today.push(notification);
      } else if (notificationDate.toDateString() === yesterday.toDateString()) {
        groupedNotifications.yesterday.push(notification);
      } else if (notificationDate >= thisWeekStart) {
        groupedNotifications.thisWeek.push(notification);
      } else {
        groupedNotifications.earlier.push(notification);
      }
    });
    return groupedNotifications;
  };

  const markAllAsSeen = async () => {
    try {
      setLoading(true);

      let allUnreadNotificationIds: any = [];
      let currentPage = 1;
      let totalPages = 1;

      // Loop through all pages to retrieve notifications
      while (currentPage <= totalPages) {
        const res = await getNotificationCount(currentPage, perpage, userid);

        if (res && res.data && res.data.data.length > 0) {
          const notifications = res.data.data[0];
          const unreadNotificationKeys = Object.keys(notifications).filter(
            (key) => notifications[key].seenStatus === false
          );
          allUnreadNotificationIds = [
            ...allUnreadNotificationIds,
            ...unreadNotificationKeys,
          ];
        }

        totalPages = res.data.totalPages || 1;
        currentPage++;
      }

      if (allUnreadNotificationIds.length === 0) {
        return;
      }

      const BATCH_SIZE = 200;
      const batches = [];

      for (let i = 0; i < allUnreadNotificationIds.length; i += BATCH_SIZE) {
        const batch = allUnreadNotificationIds
          .slice(i, i + BATCH_SIZE)
          .map((notificationId: any) => ({
            [notificationId]: true,
          }));
        batches.push(updateNotificationCount(batch));
      }

      await Promise.all(batches);
      setUnreadNotifications([]);

      if (onNotificationCountUpdate) {
        onNotificationCountUpdate(0);
      }
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    } finally {
      setLoading(false);
    }
  };

  const markIndividualAsRead = async (notificationId?: string) => {
    try {
      if (notificationId) {
        const result = [{ [notificationId]: true }];
        await updateNotificationCount(result);
        setUnreadNotifications((prevIds) => {
          const newIds = prevIds.filter((id) => id !== notificationId);
          if (onNotificationCountUpdate) {
            onNotificationCountUpdate(newIds.length);
          }
          return newIds;
        });
      }
    } catch (error) {
      console.error("Error marking seen:", error);
    }
  };

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;
      scrollPositionRef.current = target.scrollTop;
      const { scrollHeight, scrollTop, clientHeight } = target;

      const isBottomReached =
        Math.ceil(scrollTop + clientHeight) >= scrollHeight - 1;

      if (isBottomReached && !loading && curPage < totalPages) {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = window.setTimeout(() => {
          setCurPage((prevPage) => prevPage + 1);
        }, 300);
      }
      
      if (tabIndex === 1) {
        markFirstNUnreadAsRead(5);
      }
    },
    [loading, curPage, totalPages, markFirstNUnreadAsRead, tabIndex]
  );

  const formatTitle = (title: any) => {
    return title
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char: any) => char.toUpperCase());
  };

  const formatTime = (timestamp: number) => {
    return moment(timestamp).format("hh:mm a");
  };

  // Memoize filtered notifications based on tabIndex
  const filteredNotifications = React.useMemo(() => {
    if (tabIndex === 1) {
      return notificationsList.filter(n => n.seenStatus === false);
    } else if (tabIndex === 2) {
      return notificationsList.filter(n => n.seenStatus === true);
    }
    return notificationsList;
  }, [tabIndex, notificationsList]);

  // Memoize grouped notifications for filtered list
  const groupedFilteredNotifications = React.useMemo(
    () => groupNotificationsByDate(filteredNotifications),
    [filteredNotifications]
  );

  useEffect(() => {
    // if (openpop && userid) {
      setLoading(true);
      setNotificationsList([]);
      setGroupedNotifications({
        today: [],
        yesterday: [],
        thisWeek: [],
        earlier: [],
      });
      setCurPage(1);
      fetchData(1, true); // Pass true to reset data
      autoMarkedRef.current = new Set();
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openpop, tabIndex, userid]);

  useEffect(() => {
    if (curPage > 1) {
      setIsFetchingMore(true);
      fetchData(curPage, false); // Pass false to append data
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curPage]);

  useEffect(() => {
    if (openpop && notificationsList.length > 0 && tabIndex === 1) {
      markFirstNUnreadAsRead(5);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsList, openpop, tabIndex]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const notificationButtons = ["All", "Unread", "Read"];

  // Tab buttons for popover mode
  const popoverTabButtons = (
    <center>
      <Box sx={{ display: 'flex', alignContent: "center", alignItems: "center", width: '90%', mt: 1, mb: 2 }}>
        <Button
          onClick={() => setTabIndex(0)}
          sx={{
            flex: 1,
            background: tabIndex === 0 ? '#604797' : '#F3F4F6',
            color: tabIndex === 0 ? '#5B4200' : '#5B4200',
            fontWeight: tabIndex === 0 ? 500 : 400,
            borderRadius: 0,
            boxShadow: 'none',
            fontSize: '14px',
            textTransform: 'none',
            '&:hover': {
              background: '#604797',
              color: '#3d2c00',
            },
          }}
        >
          All
        </Button>
        <Button
          onClick={() => setTabIndex(1)}
          sx={{
            flex: 1,
            background: tabIndex === 1 ? '#604797' : '#F3F4F6',
            color: tabIndex === 1 ? '#5B4200' : '#5B4200',
            fontWeight: tabIndex === 1 ? 500 : 400,
            borderRadius: 0,
            boxShadow: 'none',
            fontSize: '14px',
            textTransform: 'none',
            '&:hover': {
              background: '#604797',
              color: '#3d2c00',
            },
          }}
        >
          Unread
        </Button>
        <Button
          onClick={() => setTabIndex(2)}
          sx={{
            flex: 1,
            background: tabIndex === 2 ? '#604797' : '#F3F4F6',
            color: tabIndex === 2 ? '#5B4200' : '#5B4200',
            fontWeight: tabIndex === 2 ? 500 : 400,
            borderRadius: 0,
            boxShadow: 'none',
            fontSize: '14px',
            textTransform: 'none',
            '&:hover': {
              background: '#604797',
              color: '#3d2c00',
            },
          }}
        >
          Read
        </Button>
      </Box>
    </center>
  );

  // Notifications list content (without tabs)
  const notificationListContent = (
      <Box 
        ref={containerRef}
        onScroll={handleScroll}
        id="scrollable-container"
        sx={{
          maxHeight: "calc(50vh - 48px)",
          overflowY: "auto",
          backgroundColor: "#F7A70",
          height: '50vh',
          "@media (max-width: 600px)": {
            maxHeight: "calc(100vh - 64px)",
          },
        }}
      >
        {loading ? (
          <div style={{ textAlign: "center" }}>
            <span>
              <div style={{ fontSize: "14px", color: "#5b4200", padding: "50px" }}>Loading notifications...</div>
            </span>
          </div>
        ) : notificationsList?.length === 0 ? (
          <div style={{ textAlign: "center" }}>
            <div>
              <img src={emptyNotification} width="200px" alt="" />
            </div>
            <div style={{ fontSize: "14px", color: "#5b4200" }}>
              You're all caught up! No new notifications at the moment.
            </div>
          </div>
        ) : (
          <>
            {/* Render notifications based on tab selection */}
            {(tabIndex === 0 ? groupedNotifications : groupedFilteredNotifications).today.length > 0 && (
              <>
                <Typography sx={{ fontSize: "10px", fontWeight: 400, textAlign: "center", mt: 1, color: "#5b4200" }}>
                  Today
                </Typography>
                {(tabIndex === 0 ? groupedNotifications : groupedFilteredNotifications).today.map((notification: any, index: number) => (
                  <div key={notification.id}>
                    <Grid container spacing={2} sx={{ mt: index === 0 ? 1 : 0, cursor: 'pointer' }} 
                      onClick={() => { 
                        markIndividualAsRead(notification.id); 
                        if (notification.url) {
                          window.location.href = notification.url;
                        }
                        if (handleClosePopover) {
                          handleClosePopover(); 
                        }
                      }}>
                      <Grid item xs={2} sm={2} md={2} lg={2} xl={2}>
                        <Box sx={{ height: '42px', width: '42px' }}>
                          <img
                            src={notification.imageUrl === "null" || !notification.imageUrl ? defultNotification : notification.imageUrl}
                            style={{ 
                              width: "40px", 
                              height: "40px", 
                              borderRadius: "10px",
                              border: notification.imageUrl === "null" || !notification.imageUrl ? undefined : "1px solid #5b4200"
                            }}
                            alt="notification"
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={7} sm={7} md={7} lg={7} xl={7} style={{ textAlign: 'left' }}>
                        <Typography sx={{ color: '#5b4200', fontWeight: 500, fontSize: '12px' }}>
                          {formatTitle(notification.title)}
                        </Typography>
                        <Typography sx={{ fontWeight: 400, fontSize: '11px', mt: 0.5, color: '#5b4200' }}>
                          {notification.body}
                        </Typography>
                      </Grid>
                      <Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
                        <Typography sx={{ fontWeight: 400, fontSize: '10px', mt: 0.5, textAlign: 'end', color: '#5b4200' }}>
                          {formatTime(notification.timestamp)}
                        </Typography>
                      </Grid>
                    </Grid>
                    <hr style={{ border: '1px solid #E6E6E8' }} />
                  </div>
                ))}
              </>
            )}
            
            {/* Similar sections for yesterday, thisWeek, and earlier */}
            {(tabIndex === 0 ? groupedNotifications : groupedFilteredNotifications).yesterday.length > 0 && (
              <>
                <Typography sx={{ fontSize: "10px", fontWeight: 400, textAlign: "center", mt: 1, color: "#5b4200" }}>
                  Yesterday
                </Typography>
                {(tabIndex === 0 ? groupedNotifications : groupedFilteredNotifications).yesterday.map((notification: any, index: number) => (
                  <div key={notification.id}>
                    <Grid container spacing={2} sx={{ mt: index === 0 ? 1 : 0, cursor: 'pointer' }} 
                      onClick={() => { 
                        markIndividualAsRead(notification.id); 
                        if (notification.url) {
                          window.location.href = notification.url;
                        }
                        if (handleClosePopover) {
                          handleClosePopover(); 
                        }
                      }}>
                      <Grid item xs={2} sm={2} md={2} lg={2} xl={2}>
                        <Box sx={{ height: '42px', width: '42px' }}>
                          <img
                            src={notification.imageUrl === "null" || !notification.imageUrl ? defultNotification : notification.imageUrl}
                            style={{ 
                              width: "40px", 
                              height: "40px", 
                              borderRadius: "10px",
                              border: notification.imageUrl === "null" || !notification.imageUrl ? undefined : "1px solid #5b4200"
                            }}
                            alt="notification"
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={7} sm={7} md={7} lg={7} xl={7} style={{ textAlign: 'left' }}>
                        <Typography sx={{ color: '#5b4200', fontWeight: 500, fontSize: '12px' }}>
                          {formatTitle(notification.title)}
                        </Typography>
                        <Typography sx={{ fontWeight: 400, fontSize: '11px', mt: 0.5, color: '#5b4200' }}>
                          {notification.body}
                        </Typography>
                      </Grid>
                      <Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
                        <Typography sx={{ fontWeight: 400, fontSize: '10px', mt: 0.5, textAlign: 'end', color: '#5b4200' }}>
                          {formatTime(notification.timestamp)}
                        </Typography>
                      </Grid>
                    </Grid>
                    <hr style={{ border: '1px solid #E6E6E8' }} />
                  </div>
                ))}
              </>
            )}

            {(tabIndex === 0 ? groupedNotifications : groupedFilteredNotifications).thisWeek.length > 0 && (
              <>
                <Typography sx={{ fontSize: "10px", fontWeight: 400, textAlign: "center", mt: 1, color: "#5b4200" }}>
                  This Week
                </Typography>
                {(tabIndex === 0 ? groupedNotifications : groupedFilteredNotifications).thisWeek.map((notification: any, index: number) => (
                  <div key={notification.id}>
                    <Grid container spacing={2} sx={{ mt: index === 0 ? 1 : 0, cursor: 'pointer' }} 
                      onClick={() => { 
                        markIndividualAsRead(notification.id); 
                        if (notification.url) {
                          window.location.href = notification.url;
                        }
                        if (handleClosePopover) {
                          handleClosePopover(); 
                        }
                      }}>
                      <Grid item xs={2} sm={2} md={2} lg={2} xl={2}>
                        <Box sx={{ height: '42px', width: '42px' }}>
                          <img
                            src={notification.imageUrl === "null" || !notification.imageUrl ? defultNotification : notification.imageUrl}
                            style={{ 
                              width: "40px", 
                              height: "40px", 
                              borderRadius: "10px",
                              border: notification.imageUrl === "null" || !notification.imageUrl ? undefined : "1px solid #5b4200"
                            }}
                            alt="notification"
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={7} sm={7} md={7} lg={7} xl={7} style={{ textAlign: 'left' }}>
                        <Typography sx={{ color: '#5b4200', fontWeight: 500, fontSize: '12px' }}>
                          {formatTitle(notification.title)}
                        </Typography>
                        <Typography sx={{ fontWeight: 400, fontSize: '11px', mt: 0.5, color: '#5b4200' }}>
                          {notification.body}
                        </Typography>
                      </Grid>
                      <Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
                        <Typography sx={{ fontWeight: 400, fontSize: '10px', mt: 0.5, textAlign: 'end', color: '#5b4200' }}>
                          {formatTime(notification.timestamp)}
                        </Typography>
                      </Grid>
                    </Grid>
                    <hr style={{ border: '1px solid #E6E6E8' }} />
                  </div>
                ))}
              </>
            )}

            {(tabIndex === 0 ? groupedNotifications : groupedFilteredNotifications).earlier.length > 0 && (
              <>
                <Typography sx={{ fontSize: "10px", fontWeight: 400, textAlign: "center", mt: 1, color: "#5b4200" }}>
                  Earlier
                </Typography>
                {(tabIndex === 0 ? groupedNotifications : groupedFilteredNotifications).earlier.map((notification: any, index: number) => (
                  <div key={notification.id}>
                    <Grid container spacing={2} sx={{ mt: index === 0 ? 1 : 0, cursor: 'pointer' }} 
                      onClick={() => { 
                        markIndividualAsRead(notification.id); 
                        if (notification.url) {
                          window.location.href = notification.url;
                        }
                        if (handleClosePopover) {
                          handleClosePopover(); 
                        }
                      }}>
                      <Grid item xs={2} sm={2} md={2} lg={2} xl={2}>
                        <Box sx={{ height: '42px', width: '42px' }}>
                          <img
                            src={notification.imageUrl === "null" || !notification.imageUrl ? defultNotification : notification.imageUrl}
                            style={{ 
                              width: "40px", 
                              height: "40px", 
                              borderRadius: "10px",
                              border: notification.imageUrl === "null" || !notification.imageUrl ? undefined : "1px solid #5b4200"
                            }}
                            alt="notification"
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={7} sm={7} md={7} lg={7} xl={7} style={{ textAlign: 'left' }}>
                        <Typography sx={{ color: '#5b4200', fontWeight: 500, fontSize: '12px' }}>
                          {formatTitle(notification.title)}
                        </Typography>
                        <Typography sx={{ fontWeight: 400, fontSize: '11px', mt: 0.5, color: '#5b4200' }}>
                          {notification.body}
                        </Typography>
                      </Grid>
                      <Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
                        <Typography sx={{ fontWeight: 400, fontSize: '10px', mt: 0.5, textAlign: 'end', color: '#5b4200' }}>
                          {formatTime(notification.timestamp)}
                        </Typography>
                      </Grid>
                    </Grid>
                    <hr style={{ border: '1px solid #E6E6E8' }} />
                  </div>
                ))}
              </>
            )}

            {isFetchingMore && (
              <div style={{ textAlign: "center", marginTop: "-20px" }}>
                <span style={{ color: "#5b4200" }}>Loading...</span>
              </div>
            )}
          </>
        )}
      </Box>
  );

  return (
    <>
      {openpop && (
        <ClickAwayListener onClickAway={handleClosePopover || (() => {})}>
          <Box>
            <Grid container spacing={2} sx={{ backgroundColor: "#F7A70", p: 2 }}>
              <Grid item xs={6} style={{ padding: "20px 20px 0px 15px", textAlign: "left" }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 500, color: '#5b4200' }}>Notifications</Typography>
              </Grid>
              <Grid item xs={6} style={{ padding: "24px 0px 0px 10px", textAlign: "right" }}>
                <Typography onClick={() => markAllAsSeen()} sx={{ fontSize: '12px', fontWeight: 400, color: '#604797', cursor: 'pointer' }}>
                  Mark all as read
                </Typography>
              </Grid>
            </Grid>
            <Box>
              {popoverTabButtons}
              {notificationListContent}
            </Box>
          </Box>
        </ClickAwayListener>
      )}
      {!openpop && (
        <Box component={"div"} padding={isMobileScreen ? "20px" : "16px"} height={"100%"} bgcolor={"#fff"}>
          <Box component={"div"} className="d-flex justify-content-between">
            {!isMobileScreen && (
              <Typography component={"h6"} sx={{ cursor: "pointer" }} variant="largemedium" color={"#373A45"} margin={"10px 0px"}>
                Notification
              </Typography>
            )}
            <Typography component={"h6"} sx={{ cursor: "pointer" }} variant="smallmedium" color={"#6A4EA6"} margin={"10px 0px"} onClick={() => markAllAsSeen()}>
              Mark all as read
            </Typography>
          </Box>
          <Box component={"div"} className="d-flex align-items-center gap-1" marginTop={"10px"}>
            {notificationButtons.map((items, index) => {
              const isActive = items === isActiveMsg;
              return (
                <Typography
                  component={"h6"}
                  onClick={() => {
                    handleMessageCategory(items);
                  }}
                  sx={{
                    padding: "8px",
                    textAlign: "center",
                    background: isActive ? "#604797" : "#F0EDF6",
                  }}
                  className="w-100"
                  key={index}
                  variant={!isActive ? "smallthin" : "smallmedium"}
                  color={!isActive ? "#604797" : "#FFFFFF"}
                >
                  {items}
                </Typography>
              );
            })}
          </Box>
          {notificationsList.length === 0 ? (
            <Box component={"div"} className="d-flex flex-column align-items-center justify-content-center" gap={"20px"} height={"70%"}>
              <Box component={"img"} src={emptyNotification} height={"180px"} width={"100%"} sx={{ objectFit: "contain" }} />
              <Typography variant="mediumthin" color={"#333333"}>
                You're all caught up! No new notifications at the moment.
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Notification list content without duplicate tabs */}
              {notificationListContent}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default AppNotifications;
